"use client";

import type { Workspace, WorkspacesResponse } from "../../types/workspaceWidget.ts";

import * as React from "react";
import { useEffect, useMemo, useRef } from "react";
import { ChevronsUpDown, HomeIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useDebouncedCallback } from "use-debounce";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify-icon/react";

import { WorkspaceSettingsDialog } from "./WorkspaceSettingsDialog";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { Button } from "@/components/ui/button.tsx";
import { axiosApiInstance } from "@/libs/axios.tsx";
import { API_ENDPOINTS } from "@/constants/api.ts";
import { cn } from "@/lib/utils.ts";
import { Input } from "@/components/ui/input.tsx";
import useUserStore from "@/features/auth/store/useUserStore.ts";
import PATHS from "@/routes/paths.ts";
import { Badge } from "@/components/ui/badge.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import useWorkspaceInformationStore from "@/features/workspaces/store/useWorkspaceInformationStore.tsx";
import useUserState from "@/hooks/useUserState.tsx";
import { CoachMarker } from "@/components/CoachMarker.tsx";
import { useCurrentWorkspaceStore } from "@/features/workspaces/store/useCurrentWorkspaceStore.tsx";

interface WorkspaceSwitcherProps {
  triggerClassName?: string;
}

export function WorkspaceSwitcher({ triggerClassName }: WorkspaceSwitcherProps) {
  const redirect = useNavigate();
  const { workspaceId } = useParams();
  const { t } = useTranslation(["workspaces", "common", "help"]);
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const { currentWorkspace, setCurrentWorkspace } = useCurrentWorkspaceStore();
  const { widgetData, setWidgetData, data: workspaceData } = useWorkspaceInformationStore();
  const { user } = useUserStore();
  const isTutor = useMemo(() => user?.role === "tutor", [user]);
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
  const [animate, setAnimate] = React.useState(false);
  const location = useLocation();
  const hasBeenSearched = useRef<boolean>(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const userState = useUserState();

  const getWorkspaces = async () => {
    try {
      setIsLoading(true);
      const { data: workspaces } = await axiosApiInstance.get<WorkspacesResponse>(
        `${API_ENDPOINTS.WORKSPACES.WIDGET}`,
        {
          params: {
            search: searchQuery,
          },
        },
      );

      setWidgetData(workspaces);
      // Only set current workspace from widget data if we don't have workspaceData for this workspaceId
      if (currentWorkspace === undefined && (!workspaceData || workspaceData.id !== workspaceId)) {
        setCurrentWorkspace(
          workspaces.data.find((workspace) => workspace.id === workspaceId) ?? null,
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedWorkspaces = useDebouncedCallback(() => {
    getWorkspaces();
  }, 200);

  useEffect(() => {
    if (
      ((searchQuery === "" && !widgetData && !hasBeenSearched.current) ||
        (searchQuery === "" && hasBeenSearched.current) ||
        searchQuery.length > 3) &&
      isOpen
    ) {
      debouncedWorkspaces();
    }
  }, [searchQuery, isOpen]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    hasBeenSearched.current = false;
    if (open) {
      setSearchQuery("");
    }
  };

  const handleWorkspaceSelect = (workspace: Workspace | undefined) => {
    setCurrentWorkspace(workspace ?? null);
    setAnimate(true);
    setTimeout(() => {
      redirect(
        isTutor
          ? `${PATHS.TUTOR.WORKSPACES.WORKSPACES}/${workspace ? workspace.id : "main"}`
          : `${PATHS.STUDENT.WORKSPACES.WORKSPACES}/${workspace ? workspace.id : "main"}`,
      );
    }, 300); // 300ms delay to allow for the animation
  };

  React.useEffect(() => {
    if (animate) {
      const timer = setTimeout(() => setAnimate(false), 300);

      return () => clearTimeout(timer);
    }
  }, [animate]);

  useEffect(() => {
    if (workspaceId === "main") {
      setCurrentWorkspace(null);
    } else if (workspaceData && workspaceData.id === workspaceId) {
      // Use workspaceData if available and matches the current workspaceId
      // Only use the same fields as Workspace type
      const workspaceFromData: Workspace = {
        id: workspaceData.id,
        name: workspaceData.name,
        logo: workspaceData.logo,
        workspace_type: workspaceData.workspace_type as "personal" | "group",
        users_count: workspaceData.members?.length || 0,
      };

      if (workspaceFromData.id !== currentWorkspace?.id) {
        setCurrentWorkspace(workspaceFromData);
      }
    } else if (widgetData) {
      const workspaceFromData = widgetData.data.find((w) => w.id === workspaceId);

      if (workspaceFromData && workspaceFromData.id !== currentWorkspace?.id) {
        setCurrentWorkspace(workspaceFromData);
      }
    } else if (!widgetData && !isOpen && workspaceId !== "main") {
      getWorkspaces();
    }
  }, [location.pathname, widgetData, workspaceData, workspaceId]);
  if (
    userState === "tutorWithoutProfile" ||
    userState === "tutorWithoutPricing" ||
    userState === "tutorWithoutPayments"
  )
    return null;

  return (
    <CoachMarker
      className="w-full"
      description={t("help:coachMarkers.workspaceSwitcher.description", {
        defaultValue:
          "Switch between different workspaces here. You can have multiple workspaces for different subjects or groups of students. Click to see all available workspaces and select the one you want to work in.",
      })}
      id="workspace-switcher"
      placement="bottom"
      title={t("help:coachMarkers.workspaceSwitcher.title", {
        defaultValue: "Workspace Switcher",
      })}
    >
      <DropdownMenu modal={true} open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <motion.div
            animate={{ scale: animate ? 1.02 : 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <Button
              className={`bg-background text-muted-foreground hover:bg-accent flex h-10 max-w-72 items-center justify-start gap-2 rounded-lg border-none px-2 text-sm ${isOpen && "bg-accent"} ${triggerClassName}`}
              variant="ghost"
            >
              <div className="flex grow items-center gap-2">
                <div className="flex h-6! w-6! items-center justify-center">
                  {currentWorkspace ? (
                    <img
                      alt={currentWorkspace?.name}
                      className="h-full w-full rounded-lg object-cover"
                      src={
                        currentWorkspace?.logo ?? "https://public-assets.ximly.app/logo-icon.svg"
                      }
                    />
                  ) : (
                    <HomeIcon className={"text-accent-foreground h-full! w-full!"} />
                  )}
                </div>
                <span className="max-w-52 grow truncate text-start text-sm font-medium max-[400px]:max-w-32">
                  {currentWorkspace ? currentWorkspace.name : t("workspaces:mainWorkspace")}
                </span>
              </div>
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </Button>
          </motion.div>
        </DropdownMenuTrigger>
        <AnimatePresence>
          <DropdownMenuContent
            asChild
            forceMount
            align="start"
            className="bg-foreground w-80 overflow-hidden border p-0 max-[1280px]:z-[1502]"
          >
            <motion.div
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{
                duration: 0.2,
                ease: [0.4, 0, 0.2, 1],
              }}
            >
              {currentWorkspace ? (
                <DropdownMenuItem
                  className="bg-side-accent flex rounded-b-none p-2"
                  onSelect={() => handleWorkspaceSelect(currentWorkspace)}
                >
                  <div className="flex items-center gap-2 rounded-md px-1">
                    <img
                      alt={currentWorkspace.name}
                      className="h-6 w-6 rounded-md"
                      src={currentWorkspace.logo || "/placeholder.svg"}
                    />
                    <div className="flex flex-1 flex-col truncate">
                      <Tooltip delayDuration={400}>
                        <TooltipTrigger
                          className={"w-full max-w-52 grow truncate text-start text-sm font-medium"}
                        >
                          {currentWorkspace.name}
                        </TooltipTrigger>
                        <TooltipContent>{currentWorkspace.name}</TooltipContent>
                      </Tooltip>
                      <span className="text-muted-foreground text-xs">
                        <span className={"capitalize"}>
                          {t(`workspaces:workspaceType.${currentWorkspace.workspace_type}`)}
                        </span>{" "}
                        • {t("workspaces:membersCount", { count: currentWorkspace.users_count })}
                      </span>
                    </div>
                  </div>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  className="p-2t bg-side-accent rounded-b-none"
                  onSelect={() => handleWorkspaceSelect(undefined)}
                >
                  <div className="flex items-center gap-2 rounded-md px-1">
                    <div className="flex h-7! w-7! items-center justify-center rounded-md bg-transparent">
                      <HomeIcon className={"text-accent-foreground h-6! w-6!"} />
                    </div>
                    <div className="relative flex flex-1 justify-between truncate">
                      <div className={"flex flex-col"}>
                        <span className="text-sm text-wrap">{t("workspaces:mainWorkspace")}</span>
                        <span className={"text-muted-foreground text-xs text-wrap"}>
                          {t("workspaces:mainWorkspaceDescription")}
                        </span>
                      </div>
                    </div>
                  </div>
                </DropdownMenuItem>
              )}
              <div className={"pb-1"}>
                <DropdownMenuSeparator className={"mt-0"} />
              </div>
              <div className="">
                <div className="mb-2 flex items-center gap-2 rounded-md px-3">
                  <Icon
                    className="text-muted-foreground"
                    height={"1rem"}
                    icon={"mdi:magnify"}
                    width={"1rem"}
                  />
                  <Input
                    className="h-6 border-0 bg-transparent px-2 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
                    placeholder={t("workspaces:searchWorkspaces")}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      if (!hasBeenSearched.current) {
                        hasBeenSearched.current = true;
                      }
                      setSearchQuery(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {currentWorkspace && (
                  <DropdownMenuItem
                    className={cn(
                      "bg-side-accent flex items-center gap-2 rounded-sm px-2 py-3",
                      !currentWorkspace && "bg-accent",
                    )}
                    onSelect={() => handleWorkspaceSelect(undefined)}
                  >
                    <div className="flex h-6 w-6 items-center justify-center rounded">
                      <HomeIcon className={"h-full! w-full!"} />
                    </div>
                    <div className="relative flex flex-1 justify-between truncate">
                      <div className={"flex w-full flex-col"}>
                        <div className="flex w-full items-center justify-between text-sm text-wrap">
                          <p>{t("workspaces:mainWorkspace")}</p>
                          <Badge
                            className={"h-fit w-fit px-1 py-0 text-[0.65rem]"}
                            variant={"outline"}
                          >
                            {t("common:default")}
                          </Badge>
                        </div>
                        <span className={"text-muted-foreground text-xs text-wrap"}>
                          {t("workspaces:mainWorkspaceDescription")}
                        </span>
                      </div>
                    </div>
                  </DropdownMenuItem>
                )}
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center px-2 py-4 text-center">
                    <div className="flex items-center justify-center p-2">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Number.POSITIVE_INFINITY,
                          ease: "linear",
                        }}
                      >
                        <Icon
                          className="text-accent-foreground"
                          height={"1.5rem"}
                          icon="mdi:loading"
                          width={"1.5rem"}
                        />
                      </motion.div>
                    </div>
                    <p className="text-sm font-medium">{t("common:loading")}</p>
                    <p className="text-muted-foreground mt-1 text-xs">{t("common:loading")}</p>
                  </div>
                ) : widgetData?.data && widgetData?.data?.length > 0 ? (
                  widgetData.data
                    .filter((workspace) => workspace.id !== currentWorkspace?.id)
                    .map((workspace) => (
                      <DropdownMenuItem
                        key={workspace.id}
                        className={cn(
                          "flex items-center gap-2 rounded-sm px-2 py-1.5",
                          currentWorkspace?.id === workspace.id && "bg-accent",
                        )}
                        onSelect={() => handleWorkspaceSelect(workspace)}
                      >
                        <div className="bg-accent/50 flex h-6 w-6 items-center justify-center rounded">
                          <img
                            alt={workspace.name}
                            className="h-full w-full rounded object-cover"
                            src={workspace.logo || "/placeholder.svg"}
                          />
                        </div>
                        <div className="flex flex-1 flex-col truncate">
                          <span className="text-sm font-medium">{workspace.name}</span>
                          <span className="text-muted-foreground text-xs">
                            <span className={"capitalize"}>
                              {t(`workspaces:workspaceType.${workspace.workspace_type}`)}
                            </span>{" "}
                            • {t("workspaces:membersCount", { count: workspace.users_count })}
                          </span>
                        </div>
                      </DropdownMenuItem>
                    ))
                ) : (
                  <div className="flex flex-col items-center justify-center px-2 py-4 text-center">
                    <div className="bg-accent flex items-center justify-center rounded-full p-1">
                      <Icon
                        className="text-muted-foreground"
                        height={"1.25rem"}
                        icon="mdi:folder-open-outline"
                        width={"1.25rem"}
                      />
                    </div>
                    <p className="text-sm font-medium">{t("workspaces:noWorkspacesAvailable")}</p>
                    <p className="text-muted-foreground mt-1 text-xs">
                      {t("workspaces:noWorkspacesDescription")}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </DropdownMenuContent>
        </AnimatePresence>
      </DropdownMenu>
      <WorkspaceSettingsDialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen} />
    </CoachMarker>
  );
}
